import { Temporal } from '@js-temporal/polyfill';

/**
 * Dates are passed around as ISO-8601 strings (`2024-06-01`) and only turned
 * into Temporal objects inside a function that needs to calculate with them.
 * See docs/technical.md.
 */
export type IsoDate = string;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoDate = (value: unknown): value is IsoDate =>
	typeof value === 'string' && ISO_DATE.test(value);

export const getNumberOfNights = (checkIn: IsoDate, checkOut: IsoDate) =>
	Temporal.PlainDate.from(checkIn).until(checkOut).days;

export const addDays = (date: IsoDate, days: number): IsoDate =>
	Temporal.PlainDate.from(date).add({ days }).toString();

/** Whole days from one date to another: positive when `to` is later, negative when it has passed. */
export const daysBetween = (from: IsoDate, to: IsoDate) =>
	Temporal.PlainDate.from(from).until(to).days;

/** Today in the browser's (or server's) local time zone. */
export const today = (): IsoDate => Temporal.Now.plainDateISO().toString();

/**
 * Pins English to en-GB so dates read "15 Sep 2026" rather than the US order a
 * bare `en` would give. Other locales format natively.
 */
const DISPLAY_LOCALES: Record<string, string> = { en: 'en-GB' };

export const displayLocale = (locale: string) =>
	DISPLAY_LOCALES[locale] ?? locale;

/**
 * ICU puts thin or no-break spaces in some formatted dates (around the dash in
 * a range, say), and which one depends on the ICU build. The server (Node) and
 * the browser can disagree, which fails hydration, so every formatted date is
 * reduced to plain spaces.
 */
const plainSpaces = (text: string) =>
	text.replace(/[\u00a0\u2009\u202f]/g, ' ');

const toUtcDate = (date: IsoDate) =>
	new Date(
		Temporal.PlainDate.from(date).toZonedDateTime('UTC').epochMilliseconds,
	);

/** `15 Sep 2026` */
export const formatDate = (date: IsoDate, locale = 'en') =>
	plainSpaces(
		new Intl.DateTimeFormat(displayLocale(locale), {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
			timeZone: 'UTC',
		}).format(toUtcDate(date)),
	);

/** `Mon 15 Sep` */
export const formatShortDate = (date: IsoDate, locale = 'en') =>
	plainSpaces(
		new Intl.DateTimeFormat(displayLocale(locale), {
			weekday: 'short',
			day: 'numeric',
			month: 'short',
			timeZone: 'UTC',
		}).format(toUtcDate(date)),
		// ICU builds differ on the comma after the weekday ("Sun, 29 Nov" in
		// Node, "Sun 29 Nov" in Chromium); drop it so hydration matches.
	).replace(/^(\p{L}+),/u, '$1');

/**
 * `12 – 15 Sep 2026`, or `30 Dec 2026 – 2 Jan 2027` when the year changes.
 * Uses Intl's range formatter so each locale gets its own conventions.
 */
export const formatDateRange = (
	checkIn: IsoDate,
	checkOut: IsoDate,
	locale = 'en',
) =>
	plainSpaces(
		new Intl.DateTimeFormat(displayLocale(locale), {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
			timeZone: 'UTC',
		}).formatRange(toUtcDate(checkIn), toUtcDate(checkOut)),
	);

/**
 * "3 days ago", "2 months ago", "1 year ago": how long ago something happened,
 * in the coarsest unit that fits. Used for review dates.
 */
export const formatRelativeDate = (
	isoDateTime: string,
	locale = 'en',
	now: Date = new Date(),
) => {
	const then = new Date(isoDateTime);
	const days = Math.max(
		0,
		Math.round((now.getTime() - then.getTime()) / 86_400_000),
	);
	const rtf = new Intl.RelativeTimeFormat(displayLocale(locale), {
		numeric: 'always',
	});
	if (days < 31) return rtf.format(-days, 'day');
	const months = Math.round(days / 30.4);
	if (months < 12) return rtf.format(-months, 'month');
	return rtf.format(-Math.round(days / 365.25), 'year');
};

/** `16:00:00` (as the platform sends check-in/out times) -> `16:00`. Anything else passes through. */
export const formatTime = (time: string): string => {
	const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
	return match ? `${match[1].padStart(2, '0')}:${match[2]}` : time;
};
